import React, { useState, useEffect } from 'react';
import { X, Users, ArrowRight } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

export default function TransferOwnershipModal({ isOpen, onClose, onSave, organization }) {
  const [members, setMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && organization) {
      loadMembers();
    }
  }, [isOpen, organization]);

  const loadMembers = async () => {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select(`
          id,
          email,
          first_name,
          last_name,
          user_role_assignments!user_role_assignments_user_id_fkey(
            system_roles(display_name, hierarchy_level)
          )
        `)
        ?.eq('tenant_id', organization?.id)
        ?.eq('is_active', true);

      if (error) throw error;

      // Filter for Business Admins and above
      const eligibleMembers = data?.filter(member => 
        member?.user_role_assignments?.some(ra => 
          ra?.system_roles?.hierarchy_level >= 80
        )
      );

      setMembers(eligibleMembers || []);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load organization members');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!selectedUserId) {
        throw new Error('Please select a new owner');
      }

      await onSave(organization?.id, selectedUserId);
      handleClose();
    } catch (err) {
      setError(err?.message || 'Failed to transfer ownership');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUserId('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <ArrowRight className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Transfer Ownership</h2>
              <p className="text-sm text-gray-600">{organization?.name}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              <strong>Warning:</strong> Transferring ownership will make the selected user the primary administrator of this organization.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4 inline mr-1" />
              Select New Owner *
            </label>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e?.target?.value)}
              required
            >
              <option value="">Choose a member...</option>
              {members?.map((member) => (
                <option key={member?.id} value={member?.id}>
                  {member?.first_name} {member?.last_name} ({member?.email})
                </option>
              ))}
            </Select>
            {members?.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                No eligible members found. Only Business Admins can become owners.
              </p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !selectedUserId}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Transferring...' : 'Transfer Ownership'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}